import {Message} from "primereact/message";
import {Toast} from "primereact/toast";
import {Tooltip} from "primereact/tooltip";
import {type RefObject, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";

import {
    type DescriptionKeyword,
    type DescriptionKeywordCategoriesMap,
    type DescriptionKeywordKeyNames,
    deriveDescriptionKeywordData,
    detokenizeText,
    parseKeywordTokens,
} from "@reva/shared/description-keywords";

import LoadingIndicator from "../../component/LoadingIndicator";
import InputTextAreaField from "../../component/paramInput/InputTextAreaField";
import {useParameterValue} from "../../component/paramInput/paramHooks";
import {useCheckPermission} from "../../hooks/permissionHooks";
import {selectAppreciationValueOrDefaultCurried, serializeKey} from "../../redux/appreciationsUtil";
import {selectDescriptionData, selectDescriptionError} from "../../redux/locationInfoSlice";
import {selectPersistenceLoading, selectReadOnly} from "../../redux/persistenceSlice";
import {useRevaSelector} from "../../redux/revaHooks";
import {selectLocationInfoDescriptionSpec} from "../../redux/specSlice";
import type {RevaRootState} from "../../redux/store";
import {PERMISSION_RULE_LOCATION_MICROMACRO_TEXT} from "../../util/permissions";
import PersistenceFinalMessage from "../object/PersistenceFinalMessage";
import SnapshotMessage from "../object/SnapshotMessage";
import {findInvalidKeywordToken, tokenizeText} from "./DescriptionKeywordUtil";
import DescriptionRichTextField, {resolveClickedRawOffset} from "./DescriptionRichTextField";

const MAX_UNDO_STEPS = 3;

// micro/macro speichern nicht mehr unter einem eigenen market_description_*-DOC_PROPERTY, sondern
// direkt unter der zugehörigen Würdigung (appreciation_location_micro/_macro) — der Text auf diesem
// Tab UND der Würdigungstext auf Tab Lage/Mikrolage bzw. Makrolage sind jetzt derselbe Wert.
const DOC_PROPERTY_APPRECIATION_LOCATION_MICRO = serializeKey({page: "location", tab: "micro"});
const DOC_PROPERTY_APPRECIATION_LOCATION_MACRO = serializeKey({page: "location", tab: "macro"});
const DOC_PROPERTY_MARKET_DESCRIPTION_MARKET = "market_description_market";

// #19165: Default der Wohnungsmarktbeschreibung kommt direkt aus dem Description-Endpoint (market_text).
const selectMarketTextDefault = (state: RevaRootState) => selectDescriptionData(state)?.market_text ?? null;
// Nur mit LOCATION_MICROMACRO_TEXT-Permission verwendet (siehe hasGeneratedDescriptionText unten) -- der
// Server liefert micro_text/macro_text nur an Nutzer mit dieser Permission, sonst bleiben sie null. Und
// bewusst NICHT selectAppreciationValueOrDefaultCurried: seit dem Merge von appreciation_location_micro/
// _macro mit diesem Tab liest das sonst denselben DOC_PROPERTY wie `parameter` -- ein bestehender Override
// würde dann fälschlich als "Default" durchgereicht (u.a. bei resetToDefault, das defaultValue re-
// tokenisiert: der Reset hätte den alten Override nur erneut gespeichert, statt auf den tatsächlich
// generierten Text zurückzusetzen).
const selectMicroTextDefault = (state: RevaRootState) => selectDescriptionData(state)?.micro_text ?? null;
const selectMacroTextDefault = (state: RevaRootState) => selectDescriptionData(state)?.macro_text ?? null;

interface HeaderIconButtonProps {
    icon: string;
    idSuffix: string;
    tooltip: string;
    onClick: () => void;
    marginRight?: boolean;
}

/** Klickbares Icon im Section-Header (Reset/Undo/Redo/Copy) mit eigenem Tooltip. */
function HeaderIconButton({icon, idSuffix, tooltip, onClick, marginRight = true}: HeaderIconButtonProps) {
    const targetClass = `tooltip-icon-${idSuffix}`;
    return (
        <>
            <i
                className={`pi ${icon} ${targetClass} text-primary cursor-pointer mt-4${marginRight ? " mr-3" : ""}`}
                data-pr-position="top"
                data-pr-tooltip={tooltip}
                onClick={onClick}
            />
            <Tooltip target={`.${targetClass}`} className="custom-tooltip tooltip-nowrap" />
        </>
    );
}

interface DescriptionFieldProps {
    label: string;
    parameter: string;
    defaultValueSelector: (state: RevaRootState) => string | null | undefined;
    toastRef: RefObject<Toast | null>;
    keywords?: DescriptionKeyword[];
    descriptionKeywordCategories?: DescriptionKeywordCategoriesMap;
    descriptionKeywordKeyNames?: DescriptionKeywordKeyNames;
}

function DescriptionSection({
    label,
    parameter,
    defaultValueSelector,
    toastRef,
    keywords,
    descriptionKeywordCategories = {},
    descriptionKeywordKeyNames = {},
}: DescriptionFieldProps) {
    const {t} = useTranslation();
    const readOnly = useSelector(selectReadOnly);
    const {parameterValues, onValueChange} = useParameterValue(parameter, "DOC_PROPERTY");
    const defaultValue = useSelector(defaultValueSelector);
    // Default-Text mit `[key:category_number]`-Platzhaltern (wie `richText`), für die Diff-
    // Hervorhebung in DescriptionRichTextField. Ohne `keywords` (macro/market) immer null.
    const defaultRichText = useMemo(
        () =>
            keywords
                ? tokenizeText(defaultValue, keywords, descriptionKeywordCategories, descriptionKeywordKeyNames)
                : null,
        [keywords, defaultValue, descriptionKeywordCategories, descriptionKeywordKeyNames],
    );
    // `richText` hält den Text so vor, wie er auch persistiert wird: mit `[key:category_number]`-
    // Platzhaltern statt ausgeschriebener Keywords. Ohne `keywords` (macro/market) bleibt er immer
    // null — für diese Felder gibt es nie eine Rich-Ansicht, unabhängig vom persistierten Wert.
    const [richText, setRichText] = useState<string | null>(() =>
        keywords ? (parameterValues.activeParameterValue ?? defaultRichText) : null,
    );
    const [freeTextModeOn, setFreeTextModeOn] = useState(false);
    const [undoStack, setUndoStack] = useState<string[]>([]);
    const [redoStack, setRedoStack] = useState<string[]>([]);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    // Vom Panel-Klick (siehe onClick unten) übernommene Cursorposition für den Fokus-Effect direkt
    // darunter -- null heißt "kein Klick auf reinen Text erkannt", dann ans Textende.
    const pendingCursorOffsetRef = useRef<number | null>(null);
    const rawValue = richText ?? parameterValues.activeParameterValue ?? defaultValue ?? "";
    // Ohne `keywords` gibt es keine Rich-Ansicht (siehe oben) -- ein trotzdem gespeicherter, marker-
    // haltiger Wert (z.B. von einem Nutzer mit LOCATION_MICROMACRO_TEXT-Permission) würde sonst als
    // rohes `[key:category_number]`-Token im Freitextfeld auftauchen. Auf reinen Text auflösen; für
    // bereits unmarkierten Text (macro/market, Default-Text) ist das ein No-Op.
    const value = keywords ? rawValue : detokenizeText(rawValue, descriptionKeywordCategories);
    const invalidToken = freeTextModeOn ? findInvalidKeywordToken(value, descriptionKeywordCategories) : null;

    // Beim Wechsel in den Freitextmodus soll das Textfeld sofort den Fokus erhalten, mit dem Cursor an
    // der Stelle, auf die geklickt wurde, ohne erkannte Klickposition (z.B. Klick auf Padding) ans Textende.
    useEffect(() => {
        if (!freeTextModeOn) return;
        const textArea = textAreaRef.current;
        if (!textArea) return;
        const offset = pendingCursorOffsetRef.current;
        pendingCursorOffsetRef.current = null;
        const cursorPosition =
            offset != null ? Math.max(0, Math.min(offset, textArea.value.length)) : textArea.value.length;
        textArea.focus();
        textArea.setSelectionRange(cursorPosition, cursorPosition);
    }, [freeTextModeOn]);

    const pushHistory = () => {
        setUndoStack((stack) => [...stack.slice(-(MAX_UNDO_STEPS - 1)), rawValue]);
        setRedoStack([]);
    };

    const applyRichTextChange = (newText: string) => {
        pushHistory();
        void onValueChange(newText || null, null);
        setRichText(newText);
    };

    const applyFreeTextChange = (newText: string) => {
        pushHistory();
        void onValueChange(newText || null, null);
        if (richText != null) setRichText(newText || null);
    };

    const copyText = async () => {
        try {
            await navigator.clipboard.writeText(detokenizeText(value, descriptionKeywordCategories) || "");
            toastRef.current?.show({severity: "info", summary: "Info", detail: t("general.dataCopied")});
        } catch (e) {
            console.warn("Could not copy description text to clipboard", e);
        }
    };

    const resetToDefault = () => {
        pushHistory();
        void onValueChange(null, null);
        setFreeTextModeOn(false);
        setRichText(defaultRichText);
    };

    const restoreRawValue = (newRawValue: string) => {
        void onValueChange(newRawValue || null, null);
        setFreeTextModeOn(false);
        setRichText(keywords ? newRawValue : null);
    };

    const undo = () => {
        if (undoStack.length === 0) return;
        const previous = undoStack[undoStack.length - 1];
        setUndoStack((stack) => stack.slice(0, -1));
        setRedoStack((stack) => [...stack.slice(-(MAX_UNDO_STEPS - 1)), rawValue]);
        restoreRawValue(previous);
    };

    const redo = () => {
        if (redoStack.length === 0) return;
        const next = redoStack[redoStack.length - 1];
        setRedoStack((stack) => stack.slice(0, -1));
        setUndoStack((stack) => [...stack.slice(-(MAX_UNDO_STEPS - 1)), rawValue]);
        restoreRawValue(next);
    };

    const isOverridden = parameterValues.activeParameterValue != null;

    return (
        <div className="mb-4">
            <div className="flex flex-row items-center table-title mb-2 pb-0">
                <h3 className="element-title mt-3 ml-2 mb-0 mr-4 flex-1">{label}</h3>
                {!readOnly && undoStack.length > 0 && (
                    <HeaderIconButton
                        icon="pi-arrow-left"
                        idSuffix={`undo-${parameter}`}
                        tooltip={t("market.description.undoTooltip")}
                        onClick={undo}
                    />
                )}
                {!readOnly && redoStack.length > 0 && (
                    <HeaderIconButton
                        icon="pi-arrow-right"
                        idSuffix={`redo-${parameter}`}
                        tooltip={t("market.description.redoTooltip")}
                        onClick={redo}
                    />
                )}
                {!readOnly && isOverridden && (
                    <HeaderIconButton
                        icon="pi-ban"
                        idSuffix={`reset-${parameter}`}
                        tooltip={t("market.description.resetToDefaultTooltip")}
                        onClick={resetToDefault}
                    />
                )}
                <HeaderIconButton
                    icon="pi-copy"
                    idSuffix={parameter}
                    tooltip={t("general.copyTextTooltip")}
                    onClick={() => void copyText()}
                    marginRight={false}
                />
            </div>
            <div
                className="element-panel !mb-0"
                onClick={
                    richText && !freeTextModeOn && !readOnly
                        ? (e) => {
                              pendingCursorOffsetRef.current = resolveClickedRawOffset(e.clientX, e.clientY);
                              setFreeTextModeOn(true);
                          }
                        : undefined
                }
            >
                {richText && !freeTextModeOn ? (
                    <DescriptionRichTextField
                        text={richText}
                        defaultText={defaultRichText}
                        readOnly={readOnly}
                        onTextChange={applyRichTextChange}
                        descriptionKeywordCategories={descriptionKeywordCategories}
                        descriptionKeywordKeyNames={descriptionKeywordKeyNames}
                    />
                ) : (
                    // @ts-expect-error InputTextAreaField.jsx has no prop types; only value/disabled/rows/onChange/onBlur/ref are actually required
                    <InputTextAreaField
                        ref={textAreaRef}
                        value={value}
                        disabled={readOnly}
                        rows={6}
                        onChange={(e: {target: {value: string}}) => applyFreeTextChange(e.target.value || "")}
                        onBlur={(e: {target: {value: string}}) => {
                            if (findInvalidKeywordToken(e.target.value, descriptionKeywordCategories)) {
                                setTimeout(() => textAreaRef.current?.focus(), 0);
                                return;
                            }
                            setFreeTextModeOn(false);
                        }}
                    />
                )}
            </div>
            {freeTextModeOn && (invalidToken != null || parseKeywordTokens(value).length > 0) && (
                <Message
                    severity={invalidToken ? "error" : "info"}
                    text={
                        invalidToken
                            ? t("market.description.freeTextInvalidTokenError", {
                                  marker: value.slice(invalidToken.matchStart, invalidToken.matchEnd),
                              })
                            : t("market.description.freeTextTokenHint")
                    }
                    className="w-full"
                    pt={{root: {className: "!p-2"}, icon: {className: "!text-sm"}, text: {className: "!text-sm"}}}
                />
            )}
        </div>
    );
}

export function TabMarketDescription() {
    const {t} = useTranslation();
    const toastRef = useRef<Toast>(null);
    // Bis zum Q2.5-Release kompatibel zur alten AVM-DB bleiben: liefert der Description-Endpoint
    // kein (oder ein leeres) market_text, den Wohnungsmarkt-Textbereich komplett weglassen.
    const hasMarketText = !!useSelector(selectMarketTextDefault);
    // Die Defaulttexte aller drei Felder stammen aus dem Description-Endpoint. Bis dessen Ergebnis da
    // ist, dürfen die Textareas nicht gerendert werden: sie würden zuerst die Würdigungs-Defaults zeigen
    // und beim Eintreffen der Antwort umspringen (und dabei eine laufende Eingabe überschreiben).
    // Wichtig: auf die Daten warten, nicht nur auf das loading-Flag — beim Aufruf per ?id=... startet der
    // Persistence-Load (und mit ihm der Description-Call) erst im Effect nach dem ersten Render, es gibt
    // also ein Fenster, in dem weder Persistenz noch Description als "loading" markiert sind.
    const persistenceLoading = useSelector(selectPersistenceLoading);
    const descriptionData = useRevaSelector(selectDescriptionData);
    // Schlägt der Endpoint fehl, bleibt es bei den Fallback-Texten; die Felder trotzdem anzeigen, damit
    // bereits gespeicherte Texte lesbar und editierbar bleiben.
    const descriptionError = useRevaSelector(selectDescriptionError);
    const descriptionSpec = useSelector(selectLocationInfoDescriptionSpec);
    const {categories: microKeywordCategories, keyNames: microKeywordKeyNames} =
        deriveDescriptionKeywordData(descriptionSpec);
    // Ohne diese Permission bleibt die Mikrolagebeschreibung ein reines Freitextfeld (Default-Text,
    // ohne Marker/Keywords) -- wie das Wohnungsmarkt-Feld weiter unten, das nie Keywords bekommt.
    const hasGeneratedDescriptionText = useCheckPermission(PERMISSION_RULE_LOCATION_MICROMACRO_TEXT);

    if (persistenceLoading || (!descriptionData && !descriptionError)) {
        return <LoadingIndicator loading={true} />;
    }

    return (
        <div className="flex flex-col h-full">
            <PersistenceFinalMessage />
            <SnapshotMessage />
            <Toast ref={toastRef} position="top-center" />
            <DescriptionSection
                label={t("market.description.macro")}
                parameter={DOC_PROPERTY_APPRECIATION_LOCATION_MACRO}
                defaultValueSelector={
                    hasGeneratedDescriptionText
                        ? selectMacroTextDefault
                        : selectAppreciationValueOrDefaultCurried({page: "location", tab: "macro"})
                }
                toastRef={toastRef}
            />
            <DescriptionSection
                label={t("market.description.micro")}
                parameter={DOC_PROPERTY_APPRECIATION_LOCATION_MICRO}
                defaultValueSelector={
                    hasGeneratedDescriptionText
                        ? selectMicroTextDefault
                        : selectAppreciationValueOrDefaultCurried({page: "location", tab: "micro"})
                }
                toastRef={toastRef}
                keywords={hasGeneratedDescriptionText ? (descriptionData?.micro_json ?? undefined) : undefined}
                descriptionKeywordCategories={microKeywordCategories}
                descriptionKeywordKeyNames={microKeywordKeyNames}
            />
            {hasMarketText && (
                <DescriptionSection
                    label={t("market.description.market")}
                    parameter={DOC_PROPERTY_MARKET_DESCRIPTION_MARKET}
                    defaultValueSelector={selectMarketTextDefault}
                    toastRef={toastRef}
                />
            )}
        </div>
    );
}

export default TabMarketDescription;
